import { shiftWindow } from '@construction/shared';

/**
 * 工地时区的日期换算。
 *
 * CLAUDE.md 的约定是「数据库存 UTC，展示按 Site.timezone 转换，跨天分组必须用工地时区」。
 * 这个文件是那条约定在前端的唯一落点 —— 之前每个页面自己拿 Intl 拼日期，
 * 拼出来的东西看着对，实际错了 7 小时。
 *
 * **踩过的坑：** 考勤页按工地时区算出「今天是 2026-09-11」，然后直接拼成
 * `2026-09-11T23:59:59.999Z` 当查询上界。但洛杉矶是 UTC-7，那一天实际是
 * `09-11T07:00Z` 到 `09-12T07:00Z` —— 区间整体偏早 7 小时，
 * **下午 5 点之后打的卡全部落在区间外**。工人打了卡，管理端考勤页是空的，
 * 而且不报任何错。日期字符串后面加个 Z 是最容易写、也最容易错的一步。
 */

/**
 * 工地当地的日期键（`YYYY-MM-DD`）。
 *
 * `en-CA` 这个 locale 的日期格式恰好就是 ISO 的 `YYYY-MM-DD`，
 * 拿它做格式化比自己拼 padStart 稳。
 */
export function siteDateKey(timeZone: string, offsetDays = 0): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone }).format(
    new Date(Date.now() + offsetDays * 86_400_000),
  );
}

/**
 * 把「工地当地的日期 + 时间」转成 UTC 时刻。
 *
 * 先当成 UTC 解析，再用该时区在那一刻的偏移量校正 —— 直接 `new Date()` 会按
 * **浏览器所在时区**解析，管理员在别的时区操作时就会补录到错误的时间。
 */
export function siteLocalToIso(date: string, time: string, timeZone: string): string {
  const naive = new Date(`${date}T${time}:00Z`);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(naive);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  // 有些实现把午夜给成 24 点
  const hour = get('hour') === 24 ? 0 : get('hour');
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
  const offset = asUtc - naive.getTime();
  return new Date(naive.getTime() - offset).toISOString();
}

/**
 * 把两个工地当地日期变成查询用的 UTC 区间（闭区间，含首尾两天全天）。
 *
 * 上界取当地 23:59 再加 59.999 秒，而不是「下界 + 24 小时 - 1 毫秒」——
 * 夏令时切换那天没有 24 小时，加满会多出或少掉一小时。
 */
export function siteDayRange(
  from: string,
  to: string,
  timeZone: string,
): { from: string; to: string } {
  const endOfDay = new Date(siteLocalToIso(to, '23:59', timeZone)).getTime() + 59_999;
  return {
    from: siteLocalToIso(from, '00:00', timeZone),
    to: new Date(endOfDay).toISOString(),
  };
}

/**
 * 查询区间前后各多取一个最长班次（CR-066，shared 的 `shiftWindow`）。
 *
 * 区间边界上的夜班，另一张卡在区间外面：不多取的话，「上周」最后一晚那班只剩上班卡、「本周」开头只剩下班卡。
 * 多取进来的卡只用来配对，画的时候按区间过滤（`buildDays` 的 `range`）。
 */
export function widenRange(range: { from: string; to: string }, maxShiftMinutes: number): { from: string; to: string } {
  const widened = shiftWindow(new Date(range.from), new Date(range.to), maxShiftMinutes);
  return { from: widened.start.toISOString(), to: widened.end.toISOString() };
}
